import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon,
  StickyNote2Outlined as StickyNote2OutlinedIcon,
} from "@mui/icons-material";
import {
  applyCalculatedRowFields,
  createBlankRow,
  ensureTrailingEditableRow,
  formatNumber,
  hasRowContent,
  toNumberOrNull,
  toTextOrEmpty,
} from "./utils";

const columns = [
  { key: "units", label: "Units", numeric: true },
  { key: "unitType", label: "Type", numeric: false },
  { key: "unitSizeSF", label: "Unit Size, SF", numeric: true },
  { key: "rentLow", label: "Rent Low", numeric: true, currency: true },
  { key: "rentHigh", label: "Rent High", numeric: true, currency: true },
  { key: "netRent", label: "Net Rent", numeric: true, currency: true },
  { key: "lowSF", label: "Low $/SF", numeric: true, currency: true, computed: true },
  { key: "highSF", label: "High $/SF", numeric: true, currency: true, computed: true },
];

const normalizeDraftValue = (row, key, drafts) => {
  const draftKey = `${row.id}:${key}`;
  if (Object.prototype.hasOwnProperty.call(drafts, draftKey)) {
    return drafts[draftKey];
  }
  const raw = row?.[key];
  if (raw === null || raw === undefined) return "";
  return String(raw);
};

const formatCurrencyCell = (value) => {
  const number = toNumberOrNull(value);
  if (number === null) return "-";
  return `$${number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const calculateTotals = (rows = []) => {
  const dataRows = (Array.isArray(rows) ? rows : []).filter((row) => hasRowContent(row));

  let totalUnits = 0;
  let totalSF = 0;

  let lowRentWeightedSum = 0;
  let lowRentWeight = 0;

  let highRentWeightedSum = 0;
  let highRentWeight = 0;

  let netRentWeightedSum = 0;
  let netRentWeight = 0;

  let lowSFWeightedSum = 0;
  let lowSFWeight = 0;

  let highSFWeightedSum = 0;
  let highSFWeight = 0;

  dataRows.forEach((row) => {
    const units = toNumberOrNull(row?.units);
    const unitSizeSF = toNumberOrNull(row?.unitSizeSF);
    const rentLow = toNumberOrNull(row?.rentLow);
    const rentHigh = toNumberOrNull(row?.rentHigh);
    const netRent = toNumberOrNull(row?.netRent);
    const lowSF = toNumberOrNull(row?.lowSF);
    const highSF = toNumberOrNull(row?.highSF);

    if (units !== null) {
      totalUnits += units;
    }

    if (units !== null && unitSizeSF !== null) {
      totalSF += units * unitSizeSF;
    }

    if (units !== null && units > 0 && rentLow !== null) {
      lowRentWeightedSum += rentLow * units;
      lowRentWeight += units;
    }

    if (units !== null && units > 0 && rentHigh !== null) {
      highRentWeightedSum += rentHigh * units;
      highRentWeight += units;
    }

    if (units !== null && units > 0 && netRent !== null) {
      netRentWeightedSum += netRent * units;
      netRentWeight += units;
    }

    if (units !== null && units > 0 && lowSF !== null) {
      lowSFWeightedSum += lowSF * units;
      lowSFWeight += units;
    }

    if (units !== null && units > 0 && highSF !== null) {
      highSFWeightedSum += highSF * units;
      highSFWeight += units;
    }
  });

  return {
    totalUnits,
    totalSF,
    averageUnitSize: totalUnits > 0 ? totalSF / totalUnits : null,
    averageRentLow: lowRentWeight > 0 ? lowRentWeightedSum / lowRentWeight : null,
    averageRentHigh: highRentWeight > 0 ? highRentWeightedSum / highRentWeight : null,
    averageNetRent: netRentWeight > 0 ? netRentWeightedSum / netRentWeight : null,
    averageLowSF: lowSFWeight > 0 ? lowSFWeightedSum / lowSFWeight : null,
    averageHighSF: highSFWeight > 0 ? highSFWeightedSum / highSFWeight : null,
  };
};

const RentalSurveyTable = ({
  rows = [],
  onRowsChange,
  hasNotes = false,
  onAddNotes,
  onOpenNotes,
}) => {
  const [drafts, setDrafts] = useState({});

  const tableRows = useMemo(
    () => ensureTrailingEditableRow(rows).map((row) => applyCalculatedRowFields(row)),
    [rows]
  );

  const totals = useMemo(() => calculateTotals(tableRows), [tableRows]);

  useEffect(() => {
    const rowIds = new Set(tableRows.map((row) => row.id));
    setDrafts((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const rowId = k.split(":")[0];
        if (rowIds.has(rowId)) {
          next[k] = v;
        }
      });
      return next;
    });
  }, [tableRows]);

  const updateRows = (nextRows) => {
    const normalized = ensureTrailingEditableRow(
      (Array.isArray(nextRows) ? nextRows : []).map((row) => applyCalculatedRowFields(row))
    );
    onRowsChange(normalized);
  };

  const handleChange = (rowId, key, rawValue) => {
    const column = columns.find((item) => item.key === key);
    if (column?.computed) {
      return;
    }

    const draftKey = `${rowId}:${key}`;

    setDrafts((prev) => ({
      ...prev,
      [draftKey]: rawValue,
    }));

    const nextRows = tableRows.map((row) => {
      if (row.id !== rowId) return row;

      const nextValue = column?.numeric ? toNumberOrNull(rawValue) : toTextOrEmpty(rawValue);
      return applyCalculatedRowFields({
        ...row,
        [key]: nextValue,
      });
    });

    updateRows(nextRows);
  };

  const handleBlur = (rowId, key) => {
    const draftKey = `${rowId}:${key}`;
    setDrafts((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, draftKey)) return prev;
      const next = { ...prev };
      delete next[draftKey];
      return next;
    });
  };

  const handleDeleteRow = (rowId) => {
    const keptRows = tableRows.filter((row) => row.id !== rowId);
    updateRows(keptRows.length > 0 ? keptRows : [createBlankRow()]);
  };

  const handleAddRow = () => {
    updateRows([...tableRows, createBlankRow()]);
  };

  return (
    <Box>
      <TableContainer
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: 2,
          overflowX: "auto",
          backgroundColor: "#fff",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.numeric ? "right" : "left"}
                  sx={{
                    fontWeight: 700,
                    fontSize: "12px",
                    color: "#334155",
                    backgroundColor: "#F8FAFC",
                    borderBottom: "1px solid #E2E8F0",
                    whiteSpace: "nowrap",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  width: 44,
                  fontWeight: 700,
                  fontSize: "12px",
                  color: "#334155",
                  backgroundColor: "#F8FAFC",
                  borderBottom: "1px solid #E2E8F0",
                }}
              />
            </TableRow>
          </TableHead>

          <TableBody>
            {tableRows.map((row, rowIndex) => {
              const canDelete = tableRows.length > 1 || hasRowContent(row);
              return (
                <TableRow key={row.id} hover>
                  {columns.map((column) => {
                    const value = normalizeDraftValue(row, column.key, drafts);

                    return (
                      <TableCell
                        key={`${row.id}-${column.key}`}
                        align={column.numeric ? "right" : "left"}
                        sx={{ borderBottom: "1px solid #F1F5F9", p: 0 }}
                      >
                        <TextField
                          size="small"
                          fullWidth
                          value={value}
                          onChange={
                            column.computed
                              ? undefined
                              : (event) => handleChange(row.id, column.key, event.target.value)
                          }
                          onBlur={() => handleBlur(row.id, column.key)}
                          placeholder={rowIndex === tableRows.length - 1 ? "" : undefined}
                          inputProps={{
                            inputMode: column.numeric ? "decimal" : "text",
                            readOnly: Boolean(column.computed),
                            style: {
                              textAlign: column.numeric ? "right" : "left",
                              fontSize: "13px",
                              fontWeight: 400,
                              fontVariantNumeric: column.numeric ? "tabular-nums" : "normal",
                            },
                          }}
                          InputProps={
                            column.currency
                              ? {
                                  startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                      <Typography sx={{ fontSize: "12px", color: "#64748B" }}>$</Typography>
                                    </InputAdornment>
                                  ),
                                }
                              : undefined
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 0,
                              minHeight: 34,
                              backgroundColor: column.computed ? "#F8FAFC" : "#FFFFFF",
                              "& fieldset": {
                                borderColor: "#E2E8F0",
                              },
                              "&:hover fieldset": {
                                borderColor: "#CBD5E1",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#3B82F6",
                              },
                              "& .MuiInputBase-input": {
                                padding: "8px 10px",
                                lineHeight: 1.2,
                              },
                            },
                          }}
                        />
                      </TableCell>
                    );
                  })}

                  <TableCell align="center" sx={{ borderBottom: "1px solid #F1F5F9", p: 0 }}>
                    <Tooltip title="Delete row">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteRow(row.id)}
                          disabled={!canDelete}
                          sx={{ color: "#94A3B8" }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow
              sx={{
                backgroundColor: "#EAF2FF",
                "& .MuiTableCell-root": {
                  py: 0.45,
                  px: 0.85,
                  lineHeight: 1.2,
                },
              }}
            >
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatNumber(totals.totalUnits, { maximumFractionDigits: 0 })}
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatNumber(totals.totalSF, { maximumFractionDigits: 0 })} SF
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatNumber(totals.averageUnitSize, { maximumFractionDigits: 0 })}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatCurrencyCell(totals.averageRentLow)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatCurrencyCell(totals.averageRentHigh)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatCurrencyCell(totals.averageNetRent)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatCurrencyCell(totals.averageLowSF)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#1D4ED8", borderTop: "1px solid #BFDBFE" }}>
                {formatCurrencyCell(totals.averageHighSF)}
              </TableCell>
              <TableCell sx={{ borderTop: "1px solid #BFDBFE" }} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Box sx={{ minWidth: 30 }}>
          {hasNotes ? (
            <Tooltip title="Open notes">
              <IconButton
                size="small"
                onClick={onOpenNotes}
                sx={{ color: "#475569", p: 0.5 }}
              >
                <StickyNote2OutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Button
            size="small"
            onClick={onAddNotes}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "12px", color: "#64748B" }}
          >
            + Note
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Add Row
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default RentalSurveyTable;
